// src/hooks/useFeatureConfig.js
// Fetches the feature-gate config once from GET /api/feature-config.
// Caches the result module-level so all components share one network request.
// Provides helpers used directly in AnalyticsPage and WhatIfPage.

import { useState, useEffect, useCallback } from 'react';
import { api } from '../utils/api.js';

// ── Default config (all open) used as fallback before fetch completes ─────────
export const DEFAULT_FEATURE_CONFIG = {
  analytics_overview_drawdown:   { label: 'Equity Curve & Drawdown',   plans: ['free','paid','lifetime'] },
  analytics_overview_heatmap:    { label: 'Monthly P&L Heatmap',       plans: ['free','paid','lifetime'] },
  analytics_execution_strategy:  { label: 'Strategy P&L Breakdown',    plans: ['free','paid','lifetime'] },
  analytics_execution_setup:     { label: 'Setup Quality by Strategy', plans: ['free','paid','lifetime'] },
  analytics_execution_pairs:     { label: 'Performance by Pair',       plans: ['free','paid','lifetime'] },
  analytics_timing_session:      { label: 'Performance by Session',    plans: ['free','paid','lifetime'] },
  analytics_timing_dow:          { label: 'Best Day of Week',          plans: ['free','paid','lifetime'] },
  analytics_timing_heatmap:      { label: 'Time × Day Heatmap',        plans: ['free','paid','lifetime'] },
  analytics_psychology_overview: { label: 'Emotion vs. Performance',   plans: ['free','paid','lifetime'] },
  analytics_psychology_winrate:  { label: 'Emotion Win Rate Ranking',  plans: ['free','paid','lifetime'] },
  analytics_psychology_pnl:      { label: 'Emotion P&L Attribution',   plans: ['free','paid','lifetime'] },
  analytics_risk_rr:             { label: 'Risk & R:R Breakdown',      plans: ['free','paid','lifetime'] },
  analytics_risk_winloss:        { label: 'Win vs. Loss Size',         plans: ['free','paid','lifetime'] },
  whatif_page:                   { label: 'What-If Simulator',         plans: ['free','paid','lifetime'] },

  // WhatIfPage — individual widgets
  whatif_filters:                { label: 'Simulator Filters',         plans: ['free','paid','lifetime'] },
  whatif_kpis:                   { label: 'KPI Cards',                 plans: ['free','paid','lifetime'] },
  whatif_curve:                  { label: 'Equity Curve',              plans: ['free','paid','lifetime'] },
  whatif_milestones:             { label: 'Milestones',                plans: ['free','paid','lifetime'] },
  whatif_compound:               { label: 'Compound Projector',        plans: ['free','paid','lifetime'] },
  whatif_setups:                 { label: 'Best Setup Finder',         plans: ['free','paid','lifetime'] },
  whatif_heatmap:                { label: 'Timing Heatmap',            plans: ['free','paid','lifetime'] },
  whatif_sizing:                 { label: 'Position Sizing Impact',    plans: ['free','paid','lifetime'] },

  // AiCoachPage — individual widgets
  ai_recovery_plan:               { label: 'Profit Leak Recovery Plan',   plans: ['free','paid','lifetime'] },
  ai_benchmarks:                  { label: 'Full Performance Benchmarks', plans: ['free','paid','lifetime'] },
  ai_whatif_scenarios:            { label: 'Full What-If Scenarios',      plans: ['free','paid','lifetime'] },
  ai_insights_full:                { label: 'Full Insights List',         plans: ['free','paid','lifetime'] },
  ai_unlimited_reports:           { label: 'Unlimited AI Reports',        plans: ['free','paid','lifetime'] },
  ai_dna_profile:                  { label: 'Trading DNA Profile',         plans: ['free','paid','lifetime'] },
};

// Module-level cache — shared across all hook instances
let _cache = null;

export function useFeatureConfig() {
  const [config, setConfig] = useState(_cache || DEFAULT_FEATURE_CONFIG);

  useEffect(() => {
    if (_cache) return; // already loaded
    api('/feature-config')
      .then(data => {
        const merged = { ...DEFAULT_FEATURE_CONFIG, ...data };
        _cache = merged;
        setConfig(merged);
      })
      .catch(() => {
        // Silently fall back to defaults — never block the user
      });
  }, []);

  /**
   * requiredPlans('analytics_timing_heatmap')
   * → ['paid', 'lifetime']   (what you pass to <FeatureGate requiredPlans={...}>)
   */
  const requiredPlans = useCallback((key) => {
    return config[key]?.plans ?? [];
  }, [config]);

  /**
   * featureLabel('analytics_timing_heatmap')
   * → 'Time × Day Heatmap'
   */
  const featureLabel = useCallback((key) => {
    return config[key]?.label ?? key;
  }, [config]);

  /**
   * canAccess('analytics_timing_heatmap', 'free')
   * → false  (free is not in ['paid','lifetime'])
   */
  const canAccess = useCallback((key, userPlan = 'free') => {
    const plans = config[key]?.plans ?? [];
    if (!plans.length) return true;
    return plans.includes(userPlan);
  }, [config]);

  return { config, requiredPlans, featureLabel, canAccess };
}